<template>
  <div v-if="value && value.length">
    <div :style="placeholderStyle"
         class="gallery-placeholder"
         ref="placeholder"/>
    <div class="gallery"
         :class="{'before-opening': isBeforeOpening, 'opening': isOpening, 'open': isOpen, 'before-closing': isBeforeClosing, 'closing': isClosing, 'closed': isClosed, 'loading': isLoading}"
         :style="galleryStyle"
         @click.prevent="isBeforeOpening || isOpening || isOpen ? close() : open()"
         ref="gallery">
      <div class="gallery-content">
        <figure>
          <div>
            <a :href="value[active].sourceImage"
               :title="value[active].description"
               target="_blank"
               :class="imgLinkStyle">
              <picture :key="'picture' + active">
                <source v-for="(image, i) in value[active].images" :key="'source' + active + ':' + i" :srcset="getSrcset(image)" :media="getMedia(image)"/>
                <img :src="getDefaultImageSrc(value[active].images)"
                     :key="'img' + active"
                     ref="image"
                     @load="imgLoaded"
                     loading="lazy"
                     :style="imgStyle"/>
              </picture>
            </a>
            <button v-if="value.length > 1 && !(isClosed && hidePagination)"
                    type="button"
                    class="gallery-control-prev"
                    :class="{'gallery-control-hidden': !isBeforeOpening && !isOpening && !isOpen && active === 0}"
                    @click.prevent.stop="previousSlide()">
              <i class="fa-light fa-chevron-left"></i>
            </button>
            <button v-if="value.length > 1 && !(isClosed && hidePagination)"
                    type="button"
                    class="gallery-control-next"
                    @click.prevent.stop="nextSlide()">
              <i class="fa-light fa-chevron-right"></i>
            </button>
            <button v-show="isBeforeOpening || isOpening || isOpen || isBeforeClosing || isClosing"
                    type="button"
                    @click.prevent.stop="close()"
                    class="gallery-control-close">
              <i class="fa-light fa-xmark"></i>
            </button>
          </div>
          <figcaption v-if="value[active] && value[active].description || value.length > 1"
                      @click.stop="">
            <div ref="text">
              <div class="row">
                <div v-if="value[active] && value[active].description"
                     v-html="value[active].description"
                     class="col-12 order-1 order-md-0 gallery-caption"
                     :class="{'col-md-8': value.length > 1 && !(isClosed && hidePagination), 'col-md-12': value.length <= 1 || (isClosed && hidePagination)}"/>
                <div v-if="value.length > 1 && !(isClosed && hidePagination)"
                     class="col-12 col-md-4 order-0 order-md-1 ms-auto gallery-indicators">
                  <button v-for="(file, index) in value"
                          :key="'dot' + index"
                          @click.prevent="jumpToSlide(index)"
                          :class="{'active': index == active}">
                    <i class="fa-solid fa-circle"/>
                  </button>
                </div>
              </div>
            </div>
          </figcaption>
        </figure>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "gallery",
  props: {
    value: {
      type: Array
    },
    defaultActive: {
      type: Number
    },
    hidePagination: {
      type: Boolean
    }
  },
  data: function () {
    return {
      active: this.defaultActive | 0,
      target: null,
      animate: false,
      goingPrev: false,
      goingNext: false,
      isBeforeOpening: false,
      isOpening: false,
      isOpen: false,
      isBeforeClosing: false,
      isClosing: false,
      isClosed: true,
      isLoading: true,
      left: null,
      top: null,
      width: null,
      height: null,
      imgWidth: null,
      imgHeight: null,
      hash: "open-" + new Date().getTime(),
      timeouts: [],
      textHeight: 0,
      windowHeight: window.innerHeight,
      transitionSpeed: 150,
      transitionInterval: null,
      transitionTimeout: null,
      slideDirection: "",
    };
  },
  mounted: function () {
    window.addEventListener("resize", this.handleResize);
  },
  created: function () {
    document.addEventListener("keydown", this.keydownListener);
  },
  beforeDestroy: function () {
    window.removeEventListener("resize", this.handleResize);
  },
  destroyed: function () {
    document.removeEventListener("keydown", this.keydownListener);
  },
  computed: {
    galleryStyle: function () {
      const style = [];
      if (this.isBeforeOpening || this.isClosing) {
        if (this.left != null) {
          style.push("left: " + this.left + "px;");
        }
        if (this.top != null) {
          style.push("top: " + this.top + "px;");
        }
      }
      if (this.width != null) {
        style.push("width: " + this.width + "px;");
      }
      if (this.height != null) {
        style.push("height: " + this.height + "px;");
      }
      return style.join(" ").trim();
    },
    placeholderStyle: function () {
      const style = [];
      if (this.isBeforeOpening || this.isOpening || this.isOpen || this.isBeforeClosing || this.isClosing) {
        if (this.width != null) {
          style.push("width: " + this.width + "px;");
        }
        if (this.height != null) {
          style.push("height: " + this.height + "px;");
        }
      } else {
        style.push("display: none;");
      }
      return style.join(" ").trim();
    },
    imgLinkStyle: function () {
      if (this.isLoading) {
        if (this.isClosed) {
          return "bg-loading-black-16";
        } else {
          return "bg-loading-white-16";
        }
      }
      return "";
    },
    imgStyle: function () {
      const style = [];
      if (this.isBeforeOpening || this.isOpening || this.isOpen || this.isBeforeClosing || this.isClosing) {
        let maxHeight = this.windowHeight - (2 * this.textHeight);
        if (maxHeight <= 0) {
          maxHeight = this.windowHeight;
        }
        // Subtracting 3rem because of the margin on .gallery, .gallery-caption and .gallery-indicator.
        style.push("max-height: calc(" + maxHeight + "px - 3rem);");
      }
      if (this.imgWidth) {
        style.push("width: " + this.imgWidth + "px;");
      }
      if (this.imgHeight) {
        style.push("height: " + this.imgHeight + "px;");
      }
      return style.join(" ").trim();
    },
    isTransitioning: function () {
      return [
        this.isBeforeOpening,
        this.isOpening,
        this.isOpen,
        this.isBeforeClosing,
        this.isClosing,
        this.isClosed,
        this.isLoading
      ];
    }
  },
  watch: {
    active: function () {
      // Save the "old" image width and height to prevent the img tag from collapsing while the new image loads.
      const rect = this.$refs.image.getBoundingClientRect();
      if (rect) {
        this.imgWidth = rect.width;
        this.imgHeight = rect.height;
      }

      this.isLoading = true;
    },
    isTransitioning: function () {
      if (this.transitionInterval) {
        clearInterval(this.transitionInterval);
        this.transitionInterval = null;
      }
      if (this.transitionTimeout) {
        clearTimeout(this.transitionTimeout);
        this.transitionTimeout = null;
      }
      this.transitionInterval = setInterval(this.setTextHeight, 10);
      this.transitionTimeout = setTimeout(() => {
        clearInterval(this.transitionInterval);
        this.transitionInterval = null;
        this.transitionTimeout = null;
      }, this.transitionSpeed);
    },
    isLoading: function (newValue) {
      // Remove the image width and height and let the size of the image decide the size of the img tag.
      if (!newValue) {
        this.imgWidth = null;
        this.imgHeight = null;
      }
    }
  },
  methods: {
    handleResize: function () {
      this.windowHeight = window.innerHeight;
    },
    setTextHeight: function () {
      const rect = this.$refs.text?.getBoundingClientRect();
      if (rect) {
        this.textHeight = rect.height;
      } else {
        this.textHeight = 0;
      }
    },
    imgLoaded: function () {
      this.isLoading = false;
      this.setTextHeight();
    },
    keydownListener: function (event) {
      if (this.isOpen) {
        if (event.keyCode == 27) {
          this.close();
        } else if (event.keyCode == 37) {
          this.previousSlide();
        } else if (event.keyCode == 39) {
          this.nextSlide();
        }
      }
    },
    jumpToSlide: function (newSlide) {
      if (this.active < newSlide) {
        this.slideDirection = 'slide-left';
      } else if (this.active > newSlide) {
        this.slideDirection = 'slide-right';
      }
      this.active = newSlide;
    },
    previousSlide: function () {
      this.slideDirection = 'slide-right';
      if (this.active > 0) {
        this.active--;
      } else {
        this.active = this.value.length - 1;
      }
    },
    nextSlide: function () {
      this.slideDirection = 'slide-left';
      if (this.active < this.value.length - 1) {
        this.active++;
      } else {
        this.active = 0;
      }
    },
    resetTargetSize: function () {
      this.left = null;
      this.top = null;
      this.width = null;
      this.height = null;
    },
    setTargetSize: function (rect, force) {
      try {
        if (this.left == null || force) {
          this.left = rect.left;
        }
        if (this.top == null || force) {
          this.top = rect.top;
        }
        if (this.width == null || force) {
          this.width = rect.width;
        }
        if (this.height == null || force) {
          this.height = rect.height;
        }
      } catch (err) {
        console.log(err);
        this.resetTargetSize();
      }
    },
    open: function () {
      this.clearTimeouts();
      this.isBeforeClosing = false;
      this.isClosing = false;
      this.isClosed = false;
      this.setTargetSize(this.$refs.gallery.getBoundingClientRect());
      location.hash = this.hash;
      this.isBeforeOpening = true;
      this.isOpening = false;
      this.isOpen = false;
      this.$nextTick(() => {
        this.timeouts.push(
            setTimeout(() => {
              this.isBeforeOpening = false;
              this.isOpening = true;
              this.timeouts.push(
                  setTimeout(() => {
                    this.isOpening = false;
                    this.isOpen = true;
                  }, this.transitionSpeed)
              );
            }, 0)
        );
      });
      window.addEventListener("popstate", this.close);
    },
    close: function () {
      // Do not allow to close unless it is open.
      if (!this.isBeforeOpening && !this.isOpening && !this.isOpen) {
        return;
      }
      this.clearTimeouts();
      this.isBeforeOpening = false;
      this.isOpening = false;
      this.isOpen = false;
      this.setTargetSize(this.$refs.placeholder.getBoundingClientRect(), true);
      this.isBeforeClosing = true;
      this.isClosing = false;
      this.isClosed = false;
      this.$nextTick(() => {
        this.timeouts.push(
            setTimeout(() => {
              if (this.defaultActive >= 0) {
                this.active = this.defaultActive;
              }
              this.isBeforeClosing = false;
              this.isClosing = true;
              this.$nextTick(() => {
                this.timeouts.push(
                    setTimeout(() => {
                      this.isClosing = false;
                      this.isClosed = true;
                      this.resetTargetSize();
                    }, this.transitionSpeed)
                );
              });
            }, 0)
        );
      });
      window.removeEventListener("popstate", this.close);
    },
    clearTimeouts: function () {
      const timeouts = this.timeouts.splice(0, this.timeouts.length);
      for (let i = 0; i < timeouts.length; i++) {
        clearTimeout(timeouts[i]);
      }
    },
    getSrcset: function (image) {
      return Vue.$images.getSrcset(image);
    },
    getMedia: function (image) {
      return Vue.$images.getMedia(image);
    },
    getDefaultImageSrc: function (images) {
      return Vue.$images.getDefaultImageSrc(images);
    }
  }
}
</script>

<style lang="less">
.gallery-transition {
  transition: all 0.15s ease;
}

.gallery {
  .gallery-transition;
  cursor: zoom-in;

  .gallery-content {
    margin: auto;
    width: auto;

    figure {
      display: inline-block;
      line-height: 0;
      margin: 0;
      padding: 0;
      width: 100%;

      & > div {
        line-height: 0; // Without this we get a gap below the link and image.
        position: relative;

        a {
          background-position: center;
          background-repeat: no-repeat;
          cursor: zoom-in;
          display: inline-block;
          width: 100%;

          img {
            width: 100%;
            max-width: 100%;
            height: auto;
            display: block;
          }
        }
      }

      figcaption {
        cursor: default;
        position: relative;

        & > div {
          width: 100%;
          word-wrap: break-word;
        }
      }
    }
  }

  .gallery-caption {
    color: var(--nm-grey-60);
    font-size: 0.875rem;
    font-weight: 500;
    margin-top: 0.75rem;
  }

  .gallery-indicators {
    margin-top: 0.75rem;
    text-align: left;

    @media (min-width: 768px) {
      text-align: right;
    }

    button {
      background-color: transparent;
      border: none;
      color: var(--nm-grey-10);
      font-size: 0.4375rem;
      margin-right: 0.75rem;
      padding: 0;
      vertical-align: middle;

      &.active {
        color: var(--nm-grey-80);
      }

      @media (min-width: 768px) {
        margin-left: 0.75rem;
        margin-right: 0;
      }
    }
  }

  .gallery-control-prev, .gallery-control-next, .gallery-control-close {
    .gallery-transition;
    background-color: transparent;
    border: none;
    color: var(--nm-white);
    line-height: 0;
    opacity: 1;
    outline: 0;
    padding: 0;
    position: absolute;
    // Above the image but below the figcaption.
    z-index: 2;

    i {
      border-radius: 50%;
      background-color: var(--nm-black);
      font-size: 1rem;
      height: 2rem;
      line-height: 2rem;
      margin: auto;
      width: 2rem;

      @media (min-width: 768px) {
        font-size: 1.25rem;
        height: 2.5rem;
        line-height: 2.5rem;
        width: 2.5rem;
      }
    }

    &:active, &:hover {
      i {
        transform: scale(1.1);
      }
    }
  }

  .gallery-control-prev, .gallery-control-next {
    bottom: 20%;
    margin: auto 0;
    top: 20%;
    width: 10%;
  }

  .gallery-control-prev {
    left: 0;
  }

  .gallery-control-next {
    right: 0;
  }

  .gallery-control-close {
    top: 0;
    right: 0;
    width: 10%;
    height: 10%;
  }

  .gallery-control-hidden {
    opacity: 0;
  }

  &.before-opening, &.opening, &.open, &.before-closing, &.closing {
    align-items: center;
    display: flex;
    position: fixed;
    z-index: 2000;

    .gallery-content {
      figure {
        & > div {
          position: initial;
        }

        a {
          img {
            width: auto;
          }
        }

        figcaption {
          & > div {
            position: absolute;
            // If the image is wide, the prev/next buttons might overlap with the figcaption, so we put that above the buttons with z-index 3.
            z-index: 3;
          }
        }
      }
    }

    &.loading {
      figcaption {
        display: none;
      }
    }
  }

  &.opening, &.open, &.before-closing, &.closing {
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
  }

  &.before-opening, &.before-closing, &.closing, &.closed {
    .gallery-control-close {
      opacity: 0;
    }
  }

  &.before-opening, &.opening, &.open {
    cursor: zoom-out;

    .gallery-content {
      figure {
        a {
          cursor: zoom-out;
        }
      }
    }
  }

  &.opening, &.open {
    background-color: rgba(0, 0, 0, 0.75);
    height: 100vh !important;
    padding: 0.75rem;
    width: 100% !important;

    .gallery-caption {
      color: var(--nm-white);
    }

    .gallery-indicators {
      button {
        color: var(--nm-grey-60);

        &.active {
          color: var(--nm-grey-10);
        }
      }
    }

    .gallery-control-close {
      opacity: 1;
    }
  }

  &.loading {
    .gallery-content {
      figure {
        & > div {
          a {
            img {
              // This allow us to see the "loading" background icon while loading.
              min-width: 2rem;
              min-height: 2rem;
            }
          }
        }
      }
    }
  }
}
</style>